/**
 * TeacherWorkHistory (BON-244) — every workshop this teacher has worked and
 * every one they're scheduled for.
 *
 * The horizon notice is the part worth understanding. Crew records only exist
 * from 2026 (BON-402), so a teacher who started in 2024 sees a history that
 * begins mid-career. Rather than let that read as "you never worked before
 * this", the page says where records begin — but only when it might actually be
 * hiding something, i.e. when the teacher's own earliest shift sits on the
 * system-wide floor.
 *
 * Pay amounts are BON-247 and deliberately absent.
 */
import { crewPositionLabel } from '../../../lib/crewPositions';
import { formatVenueDate } from '../../../utils/venueTime';
import { BucketError, BucketLoading, SectionCard } from '../regional/BucketStates';

import { useTeacherHistory, type WorkHistoryEntry } from './teacherApi';

/**
 * Whether older work might be missing rather than absent.
 *
 * True only when the teacher's earliest recorded shift is the earliest record
 * in the whole system — that's the case where we genuinely cannot tell whether
 * they worked before it. A teacher whose first shift is later than the floor
 * has a complete history and needs no caveat.
 */
export function historyMayBeIncomplete(
  entries: WorkHistoryEntry[],
  recordsBeginIso: string | null,
): boolean {
  if (recordsBeginIso === null || entries.length === 0) return false;
  const earliest = entries.reduce((a, b) => (a.startsAtIso < b.startsAtIso ? a : b));
  return earliest.startsAtIso.slice(0, 10) <= recordsBeginIso;
}

/**
 * "12 Mar 2026" for a pure date-only value (the crew-records FLOOR, a
 * `YYYY-MM-DD` with no zone). Formatted in UTC because the input is a plain
 * calendar date, not a workshop instant — see `formatEntryDate` for the
 * venue-local path each row's own date takes.
 */
export function formatHistoryDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    // eslint-disable-next-line no-restricted-syntax -- pure date-only floor (YYYY-MM-DD), not a workshop time
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

/**
 * A history row's date from its VENUE-LOCAL wall clock (BON-386) — a date-only
 * render off the absolute instant shows the wrong day for evening workshops in
 * western zones. Falls back to the UTC instant when no local time is stored.
 */
export function formatEntryDate(entry: WorkHistoryEntry): string {
  return entry.localStartsAtIso
    ? formatVenueDate(entry.localStartsAtIso, 'd MMM yyyy')
    : formatHistoryDate(entry.startsAtIso);
}

function roleLabel(position: string): string {
  return crewPositionLabel(position);
}

function HistoryRows({ entries, emptyText }: { entries: WorkHistoryEntry[]; emptyText: string }) {
  if (entries.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>{emptyText}</div>;
  }
  return (
    <table className="nb-tbl">
      <thead>
        <tr>
          <th>Date</th>
          <th>Venue</th>
          <th>Workshop</th>
          <th>Role</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={`${e.workshopId}-${e.position}`}>
            <td style={{ whiteSpace: 'nowrap' }}>{formatEntryDate(e)}</td>
            <td>
              <span style={{ fontWeight: 600 }}>{e.venueName}</span>
              {e.venueCity && <span style={{ color: 'var(--nb-subtle)' }}> · {e.venueCity}</span>}
            </td>
            <td className="nb-muted">{e.workshopTypeName ?? '—'}</td>
            <td>{roleLabel(e.position)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function TeacherWorkHistory() {
  const q = useTeacherHistory();

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  const { past, upcoming, recordsBeginIso } = q.data.data;
  const incomplete = historyMayBeIncomplete(past, recordsBeginIso);

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 780 }}>
      <SectionCard title={`Worked · ${past.length}`}>
        <HistoryRows
          entries={past}
          emptyText="No worked workshops recorded yet. Shifts appear here after they happen."
        />
        {incomplete && recordsBeginIso && (
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--nb-faint)' }}>
            Crew records in Nebari begin {formatHistoryDate(`${recordsBeginIso}T00:00:00.000Z`)}. If
            you worked before then, those shifts aren’t here yet.
          </p>
        )}
      </SectionCard>

      <SectionCard title={`Scheduled · ${upcoming.length}`}>
        <HistoryRows
          entries={upcoming}
          emptyText="Nothing scheduled. Shifts you’re confirmed for show up here."
        />
      </SectionCard>
    </div>
  );
}
